import { PROFILE_V2_FIELDS, ValidProfileFields } from 'Services/ProfileService/constants';

import { RelationshipIntent } from 'Store/entities/types';
import type { AvailableDescriptors } from 'Store/profile/types';

import type { Descriptor, DescriptorIcon } from './user.types';

export const getDefaultProfileV2Fields = (): ValidProfileFields[] =>
  // Can add new levers here
  [...PROFILE_V2_FIELDS].filter(Boolean) as ValidProfileFields[];

export const formatDescriptorsToRelationshipIntent = (descriptors: Descriptor[] | undefined) =>
  descriptors?.map((descriptor) => {
    // will be single selection
    const selectedItem = descriptor.choice_selections?.[0];

    return {
      ...descriptor,
      ...selectedItem,
      image_url: selectedItem?.icon_urls?.[selectedItem.icon_urls.length - 1].url,
      title_text: descriptor.name,
      body_text: selectedItem?.name,
    } as unknown as RelationshipIntent;
  })?.[0];

/**
 * Transforms a list of descriptor objects into a single object keyed by descriptor IDs.
 * Organizes descriptors by reducing the input array into an object where each key corresponds
 * to a descriptor ID. Each value under these keys includes the descriptor's name (or background
 * text if the name is missing), icons, choices, and the descriptor ID itself.
 *
 * @param availableDescriptors An array of objects, each representing a section containing multiple descriptors.
 * @returns An object where each key is a descriptor ID and each value is an object containing the descriptor's details.
 */
export const formatDescriptorsById = (availableDescriptors: AvailableDescriptors) =>
  availableDescriptors?.reduce((keyBy, availableDescriptor) => {
    const { descriptors } = availableDescriptor;
    const items = descriptors.reduce(
      (acc, descriptor) => ({
        ...acc,
        [descriptor.id]: {
          name: descriptor.name || descriptor.background_text,
          icons: descriptor.icon_urls,
          id: descriptor.id,
          choices: descriptor.choices,
        },
      }),
      {}
    );

    return { ...keyBy, ...items };
  }, {});

/**
 * Converts a collection of descriptor objects into a flat list of preference objects,
 * each representing a potential user preference with details necessary for UI rendering or further processing.
 * Each preference object includes the descriptor's name (or its background text if the name is absent),
 * icons, ID, and a choices object where choice names are mapped to their IDs.
 *
 * @param availableDescriptors An array of objects, each containing a `descriptors` array.
 * @returns A list of preference objects, each containing properties like name, icons, ID, and a simplified object of choices.
 */
export const convertDescriptorsToListOfPreferences = (availableDescriptors: AvailableDescriptors) =>
  availableDescriptors.reduce(
    (
      acc: {
        name?: string;
        icons: DescriptorIcon[];
        id: string;
        choices?: { [key: number]: string };
      }[],
      preference: {
        descriptors: Descriptor[];
      }
    ) => {
      const { descriptors } = preference;
      const items = descriptors.map((desc) => ({
        name: desc.name || desc.background_text,
        icons: desc.icon_urls,
        id: desc.id,
        choices: desc.choices?.reduce((store, c) => ({ ...store, [c.id]: c.name }), {}),
      }));

      return [...acc, ...items];
    },
    []
  );
